// Real Estate hub — Commissions tab (ticket 206, batch 206-3).
// The ledger is paged and filtered on the server; the three totals cover the whole filtered
// set, not only the page on screen, and the CSV export uses the same filters.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { api, downloadWithAuth } from '../api';
import { useAuth } from '../AuthContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TableScroller } from '@/components/ui/table-scroller';
import { errorMessage } from '../errors';
import PickSelect from '../shared/PickSelect';
import { formatDate } from '../kit/time';
import { fmt$ } from './format';

const PAGE_SIZE = 25;
const ALL = 'all';
const PAYOUT_OPTIONS = [
  { value: ALL, label: 'All payouts' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'held', label: 'Held' },
];

function filterParams(f) {
  const p = {};
  if (f.agent && f.agent !== ALL) p.agent_id = f.agent;
  if (f.payout && f.payout !== ALL) p.payout_status = f.payout;
  if (f.from) p.date_from = f.from;
  if (f.to) p.date_to = f.to;
  return p;
}

export default function HubCommissionsTab() {
  const auth = useAuth();
  const isAdmin = auth?.user?.role === 'admin';
  const [filters, setFilters] = useState({ agent: ALL, payout: ALL, from: '', to: '' });
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [agents, setAgents] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/re/agents').then((r) => setAgents(r.data || [])).catch(() => {});
  }, [isAdmin]);

  // Only the newest request may fill the table: a slow answer for an old filter is dropped.
  const latest = useRef(0);
  const load = useCallback(() => {
    const mine = ++latest.current;
    setError('');
    return api.get('/re/commissions/search', { params: { ...filterParams(filters), skip: page * PAGE_SIZE, limit: PAGE_SIZE } })
      .then((r) => { if (mine === latest.current) setData(r.data); })
      .catch((e) => { if (mine === latest.current) setError(errorMessage(e, 'Could not load the commission ledger')); });
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  const change = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(0); };
  const exportCsv = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(filterParams(filters)).toString();
      await downloadWithAuth(`/re/commissions/export.csv${qs ? `?${qs}` : ''}`, 'commissions.csv');
    } finally { setExporting(false); }
  };

  const agentOptions = [{ value: ALL, label: 'All agents' }, ...agents.filter((a) => a.user_id).map((a) => ({ value: a.user_id, label: a.name || `License #${a.license_number}` }))];
  const totals = data?.totals || { gross: 0, agent: 0, brokerage: 0 };
  const total = data?.total || 0;
  const first = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const last = Math.min(total, (page + 1) * PAGE_SIZE);
  const filtered = filters.agent !== ALL || filters.payout !== ALL || filters.from || filters.to;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Gross Commission" value={fmt$(totals.gross)} color="#070B5F" />
        <Stat label="Net to Agents" value={fmt$(totals.agent)} color="#6BA82E" />
        <Stat label="Brokerage Retained" value={fmt$(totals.brokerage)} color="#f59e0b" />
      </div>
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="font-serif font-semibold" style={{ color: '#070B5F' }}>Commission Ledger</div>
          <button onClick={exportCsv} disabled={exporting || !total} data-testid="commissions-export" className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 inline-flex items-center gap-1.5 disabled:opacity-50">
            <Download size={12} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="commissions-filters">
          {isAdmin && <div className="w-48"><PickSelect label="Agent" value={filters.agent} onChange={(v) => change({ agent: v })} options={agentOptions} testId="commissions-filter-agent" /></div>}
          <div className="w-40"><PickSelect label="Payout" value={filters.payout} onChange={(v) => change({ payout: v })} options={PAYOUT_OPTIONS} testId="commissions-filter-payout" /></div>
          <div><Label htmlFor="comm-from" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">From</Label><Input id="comm-from" type="date" value={filters.from} onChange={(e) => change({ from: e.target.value })} className="mt-1" data-testid="commissions-filter-from" /></div>
          <div><Label htmlFor="comm-to" className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">To</Label><Input id="comm-to" type="date" value={filters.to} onChange={(e) => change({ to: e.target.value })} className="mt-1" data-testid="commissions-filter-to" /></div>
          {filtered && <button onClick={() => change({ agent: ALL, payout: ALL, from: '', to: '' })} className="text-xs underline text-slate-500 pb-2" data-testid="commissions-clear">Clear filters</button>}
        </div>
        {error && <div className="text-sm text-red-600" data-testid="commissions-error">{error} <button type="button" onClick={load} className="underline">Retry</button></div>}
        {!error && !data && <div className="text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {!error && data && total === 0 && (
          <div className="p-8 border border-dashed border-slate-200 rounded-xl text-center text-sm text-slate-400" data-testid="commissions-empty">
            {filtered ? 'No ledger entries match these filters.' : 'No commissions posted. Entries auto-create when a transaction moves to Closed.'}
          </div>
        )}
        {!error && data && total > 0 && (
          <>
            <TableScroller><table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-100">
                  <th className="pb-2">Date</th><th className="pb-2 pr-4 min-w-[8rem]">Transaction</th>{isAdmin && <th className="pb-2 pr-4">Agent</th>}<th className="pb-2 text-right">Gross</th><th className="pb-2 pl-3 text-right">Agent Net</th><th className="pb-2 text-right">Brokerage</th><th className="pb-2 text-right">Payout</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.entry_id} className="border-b border-slate-50" data-testid={`commission-row-${e.entry_id}`}>
                    <td className="py-2 text-xs">{formatDate(e.created_at)}</td>
                    <td className="py-2 pr-4 text-xs">
                      <Link to={`/portal/real-estate/transactions/${e.transaction_id}`} className="underline" style={{ color: '#070B5F' }}>{e.transaction_label || e.transaction_id.slice(-6)}</Link>
                    </td>
                    {isAdmin && <td className="py-2 pr-4 text-xs">{e.agent_name || '—'}</td>}
                    <td className="py-2 text-right font-serif">{fmt$(e.gross_commission)}</td>
                    <td className="py-2 text-right font-serif font-bold" style={{ color: '#6BA82E' }}>{fmt$(e.net_to_agent)}</td>
                    <td className="py-2 text-right">{fmt$(e.brokerage_share)}</td>
                    <td className="py-2 text-right text-xs uppercase tracking-widest">{e.payout_status}</td>
                  </tr>
                ))}
              </tbody>
            </table></TableScroller>
            <div className="flex items-center justify-between mt-3 text-xs text-slate-500" data-testid="commissions-pager">
              <span>{first}–{last} of {total}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => p - 1)} disabled={page === 0} data-testid="commissions-prev" className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Previous</button>
                <button onClick={() => setPage((p) => p + 1)} disabled={last >= total} data-testid="commissions-next" className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="min-w-0 bg-white rounded-2xl p-3 sm:p-5 border border-slate-100 shadow-sm">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-serif text-xl sm:text-3xl font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
